import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  Input,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { distinctUntilChanged, filter, fromEvent, map, startWith } from 'rxjs';

import { NavigationEnd, Router } from '@angular/router';
import * as fromStore from 'src/app/store/';
import { environment } from 'src/environments/environment';
import { HeaderService } from '../../../core/services/header';
import {
  accountNavigation,
  headerNavigation,
} from './header-navigation/header-navigation';
import { NavigationRecordService } from './navigation-record/navigation-record.service';
import { Navigation } from './navigation-record/navigation-record.types';

type MobileNavLevel = {
  title: string;
  items: Navigation.Record[];
};

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  providers: [NavigationRecordService],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class HeaderComponent implements OnInit {
  @Input() browserSize = '';

  readonly header$ = inject(HeaderService);

  private readonly destroyRef = inject(DestroyRef);

  readonly navigationRecordService = inject(NavigationRecordService);

  readonly leftNav$ =
    this.navigationRecordService.compileNavigation(headerNavigation);

  readonly rightNav$ =
    this.navigationRecordService.compileNavigation(accountNavigation);

  readonly leftNav = toSignal(this.leftNav$, { initialValue: [] });

  readonly rightNav = toSignal(this.rightNav$, { initialValue: [] });

  readonly user$ = inject<Store<fromStore.State>>(Store).select(
    fromStore.getCurrentUser,
  );

  readonly mobileMenuOpen = signal(false);

  readonly mobileNavStack = signal<MobileNavLevel[]>([]);

  readonly mobileCurrentNav = signal<Navigation.Record[]>([]);

  readonly mobileNavTitle = signal('');

  readonly mobileRootNav = computed(() => this.leftNav());

  readonly siteUrl = environment.siteUrl;

  readonly viewport = toSignal(
    fromEvent(window, 'resize').pipe(
      map(() => window.innerWidth),
      startWith(window.innerWidth),
      map((width) => ({
        underLg: width <= 1024,
      })),
      distinctUntilChanged((prev, curr) => prev.underLg === curr.underLg),
    ),
    { initialValue: { underLg: window.innerWidth <= 1024 } },
  );

  private readonly router = inject(Router);

  ngOnInit(): void {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        if (this.mobileMenuOpen()) {
          this.closeMobileMenu();
        }
      });
  }

  openMobileMenu(): void {
    this.mobileCurrentNav.set(this.mobileRootNav());
    this.mobileNavStack.set([]);
    this.mobileNavTitle.set('');
    this.mobileMenuOpen.set(true);
    document.body.classList.add('overflow-hidden');
  }

  closeMobileMenu(): void {
    this.mobileMenuOpen.set(false);
    this.mobileNavStack.set([]);
    this.mobileCurrentNav.set([]);
    this.mobileNavTitle.set('');
    document.body.classList.remove('overflow-hidden');
  }

  onMobileNavigate(item: Navigation.Record): void {
    if ('children' in item && item.children?.length) {
      this.mobileNavStack.update((stack) => [
        ...stack,
        {
          title: this.mobileNavTitle(),
          items: this.mobileCurrentNav(),
        },
      ]);

      this.mobileCurrentNav.set(item.children);
      this.mobileNavTitle.set(item.label || item.alias || '');
      return;
    }

    this.closeMobileMenu();
  }

  handleMobileBack(): void {
    const stack = this.mobileNavStack();

    if (!stack.length) {
      this.closeMobileMenu();
      return;
    }

    const previous = stack[stack.length - 1];
    this.mobileNavStack.set(stack.slice(0, -1));
    this.mobileCurrentNav.set(previous.items);
    this.mobileNavTitle.set(previous.title);
  }

  openMobileRootSection(item: Navigation.Record): void {
    if ('children' in item && item.children?.length) {
      this.mobileNavStack.set([
        {
          title: 'Menu',
          items: this.mobileRootNav(),
        },
      ]);
      this.mobileCurrentNav.set(item.children);
      this.mobileNavTitle.set(item.label || item.alias || '');
      return;
    }

    this.closeMobileMenu();
  }
}
